// ─────────────────────────────────────────────────────────────
// LinksColumn — middle pane.
// Lists all LinkDefs grouped by source question.
// Has the "+ Add link" button at the top.
// ─────────────────────────────────────────────────────────────

import * as React from 'react';
import { useMemo } from 'react';
import {
  Box, Stack, Typography, Chip, Card, IconButton, Button, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalculateIcon from '@mui/icons-material/Calculate';
import FunctionsIcon from '@mui/icons-material/Functions';

import type { SDM, LinkDef, OID, ItemDef, CodeList, Task } from '../types';
import { linksFor, humanExpression, readableTrigger } from '../expressions';
import { MetaKeyChip, ModeBadge, TypeIcon } from '../Bits';

export interface LinksColumnProps {
  sdm: SDM;
  links: LinkDef[];
  selectedOID: OID | null;
  hoveredTaskId: number | null;
  taskById: Record<number, Task>;
  itemByOid: Record<OID, ItemDef>;
  onEdit: (l: LinkDef) => void;
  onDelete: (oid: OID) => void;
  onAddLink: () => void;
  onShowAll: () => void;
}

export function LinksColumn({
  sdm, links, selectedOID, hoveredTaskId, taskById, itemByOid,
  onEdit, onDelete, onAddLink, onShowAll,
}: LinksColumnProps): JSX.Element {
  const codeByOid = useMemo(
    () => Object.fromEntries(sdm.TemplateVersion[0].CodeList.map(c => [c.OID, c])) as Record<OID, CodeList>,
    [sdm],
  );

  const visible = useMemo(() => {
    if (!selectedOID && !hoveredTaskId) return links;
    return links.filter(l =>
      (!!selectedOID  && l.sourceItemOID === selectedOID) ||
      (!!hoveredTaskId && l.targetTaskIds.includes(hoveredTaskId)),
    );
  }, [links, selectedOID, hoveredTaskId]);

  const grouped = useMemo(() => {
    const order: OID[] = [];
    const byItem: Record<OID, LinkDef[]> = {};
    visible.forEach(l => {
      const key = l.sourceItemOID ?? '__formula';
      if (!byItem[key]) { byItem[key] = []; order.push(key); }
      byItem[key].push(l);
    });
    return { order, byItem };
  }, [visible]);

  return (
    <Box sx={{
      height: '100%', overflow: 'auto', bgcolor: 'background.default',
      borderRight: 1, borderColor: 'divider',
    }}>
      <Box sx={{
        p: 2, pb: 1.25, borderBottom: 1, borderColor: 'divider',
        position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 2,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Box>
            <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
              Active links &middot; {links.length}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {selectedOID ? 'Filtered to selected question' :
               hoveredTaskId ? 'Hovering task — showing affecting links' :
               'Drag pills or use the button below'}
            </Typography>
          </Box>
          {links.length > 0 && (
            <Tooltip title={selectedOID || hoveredTaskId ? 'Show all links' : 'All links'}>
              <Chip
                label={links.length}
                icon={<LinkIcon sx={{ fontSize: 14 }} />}
                size="small" color="secondary"
                onClick={onShowAll}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
          )}
        </Stack>
        <Box sx={{ mt: 1.5 }}>
          <Button
            variant="contained" color="primary" size="medium"
            startIcon={<AddIcon />}
            fullWidth
            onClick={onAddLink}
          >
            Add link
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {grouped.order.length === 0 && (
          <Box sx={{
            p: 3.5, textAlign: 'center',
            border: 1, borderStyle: 'dashed', borderColor: 'divider',
            borderRadius: 2, bgcolor: 'background.paper',
          }}>
            <LinkIcon sx={{ color: 'action.disabled', fontSize: 28, mb: 1 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
              {selectedOID || hoveredTaskId ? 'No links match this filter.' : 'No links yet.'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Drag an answer pill onto a task, or click <b>+ Add link</b> above.
            </Typography>
          </Box>
        )}

        {grouped.order.map(itemOID => {
          const item = itemByOid[itemOID];
          const cl = item?.CodeListRef ? codeByOid[item.CodeListRef.CodeListOID] : null;
          const ls = grouped.byItem[itemOID];
          return (
            <Box key={itemOID} sx={{ mb: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                {item && <TypeIcon type={item.DataType} />}
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, color: 'text.secondary' }}>
                  {item?.Question?.TranslatedText?.text ?? '(derived formula)'}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'text.disabled' }}>
                  {itemOID}
                </Typography>
              </Stack>
              {ls.map(l => (
                <LinkCard key={l.OID}
                  link={l} item={item} codeList={cl}
                  taskById={taskById}
                  itemByOid={itemByOid}
                  onEdit={() => onEdit(l)}
                  onDelete={() => onDelete(l.OID)}
                />
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── LinkCard ─────────────────────────────────────────────────

interface LinkCardProps {
  link: LinkDef;
  item: ItemDef | undefined;
  codeList: CodeList | null;
  taskById: Record<number, Task>;
  itemByOid: Record<OID, ItemDef>;
  onEdit: () => void;
  onDelete: () => void;
}

const OP_LABEL: Record<string, string> = {
  eq: 'is', ne: 'is not', gt: '>', gte: '≥', lt: '<', lte: '≤',
  contains: 'includes', not_contains: 'excludes',
  is_empty: 'is empty', is_not_empty: 'is answered',
};

function LinkCard({ link, item, codeList, taskById, itemByOid, onEdit, onDelete }: LinkCardProps): JSX.Element {
  const isFormula = link.mode === 'formula';
  // A remove link authored via the condition builder carries its rules in remove.branches.
  const removeBranches = link.mode === 'remove' ? link.remove?.branches ?? null : null;

  const populateText = (() => {
    const p = link.populate;
    if (!p) return null;
    return p.formulaHuman
      ?? humanExpression(p.expression, itemByOid)
      ?? null;
  })();

  return (
    <Card
      variant="outlined"
      sx={{
        p: 1.25, mb: 0.75,
        ...(link._implicit ? { borderStyle: 'dashed', bgcolor: 'action.hover' } : {}),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }} flexWrap="wrap">
        {link._rawOnly ? (
          <>
            <Chip
              size="small"
              icon={<FunctionsIcon sx={{ fontSize: 12 }} />}
              label="Expression"
              sx={{
                height: 20, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                bgcolor: 'secondary.50', color: 'secondary.dark', '& .MuiChip-label': { px: 0.75 },
              }}
            />
            {link.targetMetaKey && <MetaKeyChip value={link.targetMetaKey} />}
            <Typography variant="caption" sx={{ flex: 1, color: 'text.disabled', fontStyle: 'italic' }}>
              raw — edit expression text
            </Typography>
            <Tooltip title="Edit expression">
              <IconButton size="small" onClick={onEdit}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
          </>
        ) : link._implicit ? (
          <>
            <Chip
              size="small"
              icon={<LinkIcon sx={{ fontSize: 12 }} />}
              label="MetaKey"
              sx={{
                height: 20, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                bgcolor: 'grey.200', color: 'text.secondary', '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <MetaKeyChip value={link.targetMetaKey} />
            <Tooltip title="Inferred from a MetaKey shared by this question item and the task(s). Not an authored link — clear the MetaKey on either side to remove.">
              <Typography variant="caption" sx={{ flex: 1, color: 'text.disabled', fontStyle: 'italic' }}>
                auto-detected — shared MetaKey
              </Typography>
            </Tooltip>
            <Tooltip title="Edit link">
              <IconButton size="small" onClick={onEdit}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
            <Tooltip title="Delete link">
              <IconButton size="small" onClick={onDelete} color="error"><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
          </>
        ) : link.mode === 'switchPlan' && link.switchPlan ? (
          <>
            <ModeBadge mode={link.mode} />
            <Typography variant="caption" sx={{ flex: 1, color: 'text.primary' }}>
              {/* itemLabel intentionally omitted: the question name is already shown once in this
                  group's header immediately above the card — passing it here too would duplicate
                  the text within this same card, which is otherwise indistinguishable from the
                  header's plain "<question>" text to a substring text query. */}
              {readableTrigger(link, codeList, undefined)} &rarr; use{' '}
              <Box component="span" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                {link.switchPlan.pickedPlanUid}
              </Box>
              , otherwise{' '}
              <Box component="span" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                {link.switchPlan.fallbackPlanUid}
              </Box>
            </Typography>
            <Tooltip title="Edit link">
              <IconButton size="small" onClick={onEdit}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
            <Tooltip title="Delete link">
              <IconButton size="small" onClick={onDelete} color="error"><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <ModeBadge mode={link.mode} />
            <Typography variant="caption" sx={{ flex: 1, color: 'text.primary' }}>
              {removeBranches?.length
                ? 'When conditions match'
                : readableTrigger(link, codeList, item?.Question?.TranslatedText?.text)}
            </Typography>
            <Tooltip title="Edit link">
              <IconButton size="small" onClick={onEdit}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
            <Tooltip title="Delete link">
              <IconButton size="small" onClick={onDelete} color="error"><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
          </>
        )}
      </Stack>

      {/* Raw expression preview (for formulas the visual builder can't model) */}
      {link._rawOnly && link._rawExpression && (
        <Box sx={{
          p: 0.75, mb: 0.75, borderRadius: 1,
          bgcolor: 'action.hover', border: 1, borderColor: 'divider',
          fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'text.secondary',
          maxHeight: 92, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {link._rawExpression}
        </Box>
      )}

      {(link.mode === 'populate' || isFormula) && !link._rawOnly && link.populate && (
        <Box sx={{
          p: 0.75, borderRadius: 1, mb: 0.75,
          bgcolor: isFormula ? 'secondary.50' : 'success.50',
          border: 1, borderColor: isFormula ? 'secondary.light' : 'success.light',
          fontSize: 12, lineHeight: 1.5,
        }}>
          <Typography variant="caption" sx={{
            display: 'inline', mr: 0.5,
            color: isFormula ? 'secondary.dark' : 'success.dark',
            fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
          }}>
            {isFormula ? 'formula → ' : 'set '}
          </Typography>
          {link.populate.formulaName && <b>{link.populate.formulaName}: </b>}
          <b>{link.populate.field?.split('.').slice(-1)[0] ?? 'quantity'}</b>
          {' = '}
          {populateText || (
            <Box component="em" sx={{ color: 'text.disabled' }}>not configured</Box>
          )}
        </Box>
      )}

      {link.mode === 'remove' && link.remove?.cascadeDependents && (
        <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mb: 0.75 }}>
          ↳ also removes dependent tasks
        </Typography>
      )}

      {/* Remove-mode condition rules preview */}
      {removeBranches && removeBranches.length > 0 && (
        <Box sx={{ my: 0.5 }}>
          {removeBranches.map((br, bi) => (
            <Box
              key={bi}
              sx={{
                fontSize: 11.5, px: 1, py: 0.4, mb: 0.25,
                bgcolor: 'error.50', border: 1, borderColor: 'error.light', borderRadius: 0.5,
              }}
            >
              <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'error.main', mr: 0.5 }}>
                {bi === 0 ? 'Delete when' : 'or when'}
              </Typography>
              {br.conditions.filter(c => c.field).map((c, ci) => (
                <Typography component="span" variant="caption" key={ci} sx={{ color: 'text.secondary' }}>
                  {ci > 0 && <b>{' '}{c.connector === 'OR' ? 'OR' : 'AND'}{' '}</b>}
                  <b>{itemByOid[c.field]?.Question?.TranslatedText?.text ?? c.field}</b>
                  {' '}{OP_LABEL[c.op] ?? c.op}
                  {c.values.length > 0 && <> {c.values.map(String).join(', ')}</>}
                </Typography>
              ))}
              {br.conditions.filter(c => c.field).length === 0 && (
                <Typography component="span" variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                  always
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Decision-table branches preview */}
      {isFormula && link.populate?.branches && link.populate.branches.length > 0 && (
        <Box sx={{ my: 0.5 }}>
          {link.populate.branches.map((br, bi) => (
            <Stack
              key={bi} direction="row" alignItems="baseline" spacing={0.75}
              sx={{
                fontSize: 11.5, px: 1, py: 0.25,
                bgcolor: bi % 2 === 0 ? 'action.hover' : 'background.paper',
                borderRadius: 0.5,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'secondary.main', flexShrink: 0 }}>
                {bi === 0 ? 'When' : 'Else when'}
              </Typography>
              <Box sx={{ flex: 1 }}>{br.conditions.length ? '...' : '—'}</Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>→</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{br.formulaTemplate || '...'}</Typography>
            </Stack>
          ))}
          {link.populate.defaultValue !== undefined && (
            <Typography variant="caption" sx={{ display: 'block', px: 1, color: 'text.secondary' }}>
              Otherwise → <b>{link.populate.defaultValue}</b>
            </Typography>
          )}
        </Box>
      )}

      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {link.targetTaskIds.map(tid => {
          const t = taskById[tid];
          return (
            <Chip
              key={tid}
              size="small"
              icon={<CalculateIcon sx={{ fontSize: 12, color: 'text.disabled' }} />}
              label={(
                <span>
                  <span style={{ color: 'inherit', opacity: 0.6, fontFamily: 'JetBrains Mono, monospace', marginRight: 4 }}>#{t?.taskNumber ?? tid}</span>
                  {t?.taskName ?? '(missing)'}
                </span>
              )}
              sx={{ height: 22, fontSize: 11 }}
            />
          );
        })}
      </Stack>
    </Card>
  );
}

